<template>
  <div class="register-page-full">
    <div class="left-panel">
      <div class="brand-area">
        <img src="@/assets/login/pc_img_logo_content_paycools.png" alt="PayCools" class="brand-logo">
      </div>
    </div>
    <div class="right-panel">
      <div class="card">
        <h3 class="card-title">Create your PayCools account</h3>

        <el-form ref="registerForm" :model="registerForm" :rules="registerRules" class="register-form" label-position="top" auto-complete="on">
          <el-form-item label="Email address" prop="email">
            <el-input v-model="registerForm.email" placeholder="Please enter your email address" />
          </el-form-item>

          <el-form-item label="" prop="verificationCode">
            <div class="verification-row">
              <el-input v-model="registerForm.verificationCode" placeholder="Please enter the verification code" class="verification-input">
                <template slot="append">
                  <el-button
                    class="send-btn link-style"
                    size="small"
                    type="text"
                    :disabled="!isEmailValid || sending || sendCountdown > 0"
                    @click.prevent="onSendClick"
                  >
                    {{ sendCountdown > 0 ? `Resend (${sendCountdown}s)` : (sending ? 'Sending...' : 'Send Verification Code') }}
                  </el-button>
                </template>
              </el-input>
            </div>
          </el-form-item>

          <el-form-item label="Password" prop="password">
            <el-input v-model="registerForm.password" :type="passwordType" placeholder="Please enter your password" show-password clearable />
          </el-form-item>

          <el-form-item label="Confirm Password" prop="confirmPassword">
            <el-input v-model="registerForm.confirmPassword" :type="passwordType" placeholder="Please enter your password again" show-password clearable />
          </el-form-item>

          <el-form-item>
            <el-checkbox v-model="agreePolicy" class="agree-checkbox">
              I have read and agree to
              <el-link class="policy-link" type="primary" underline @click.stop.prevent="openServices"> Services Agreements </el-link>
              <br>
              and
              <el-link class="policy-link" type="primary" underline @click.stop.prevent="openPrivacy"> Privacy Policy </el-link>
            </el-checkbox>
          </el-form-item>

          <el-form-item class="create-row">
            <el-button type="primary" class="create-btn full" :disabled="!agreePolicy || creating" :loading="creating" @click.prevent="handleCreate">Register</el-button>
          </el-form-item>

          <div class="signin-line">
            Already have an account?
            <el-link type="primary" @click.prevent="goLogin">Sign in.</el-link>
          </div>
        </el-form>

        <p class="footer-copy">© 2026 PayCools (Payment Innovations Inc.)</p>
      </div>
    </div>
    <!-- 注册成功弹窗 -->
    <el-dialog :visible.sync="showSuccessDialog" width="420px" :before-close="onSuccessDialogClose" :close-on-click-modal="false" center>
      <div style="text-align:center; padding: 24px 12px;">
        <div style="font-size:18px; color:#1D3A71; font-weight:600; margin-bottom:12px;">Registration Successful</div>
        <div style="color:#6B7280; margin-bottom:18px;">You will be redirected to Login in {{ successCountdown }}s</div>
        <el-button type="primary" @click="goToLoginFromDialog">Go to Login ({{ successCountdown }}s)</el-button>
      </div>
    </el-dialog>
    <!-- Privacy Policy component (self-contained dialog) -->
    <PrivacyPolicy :visible.sync="showPrivacyDialog" />
    <!-- Services Agreements component (self-contained dialog) -->
    <ServicesAgreements :visible.sync="showServicesDialog" />
  </div>
</template>

<script>
import PrivacyPolicy from './privacy-policy.vue'
import ServicesAgreements from './services-agreements.vue'
import { sendSelfRegisterOtp } from '@/api/user'
import { cryptoJSAesEncrypt } from '@/utils/encrypt'
import { getDeviceHeader } from '@/utils/helper'

export default {
  name: 'Register',
  components: {
    PrivacyPolicy,
    ServicesAgreements
  },
  data() {
    const verificationCode = (rule, value, callback) => {
      if (this.checkEmailCodeFlag && !value) {
        return callback(new Error('Please input MFA Verification Code'))
      }
      if (value.length < 6 || value.length > 6) {
        return callback(new Error('Please enter 6 digit valid MFA Verification code'))
      }
      return callback()
    }
    const validateEmail = (rule, value, callback) => {
      // use a simple, robust email check: non-empty local, '@', domain with a dot
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!value) {
        return callback(new Error('Please enter your email address'))
      }
      if (!emailRegex.test(value)) {
        return callback(new Error('Please enter a valid email address'))
      }
      return callback()
    }

    const validatePass = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('password must between 8 ~ 15 character'))
      }
      if (value.length < 8 || value.length > 15) {
        return callback(new Error('password must between 8 ~ 15 character'))
      }
      const regex = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@%*&_])[A-Za-z\\d@%*&_]{8,15}$')
      if (!regex.test(value)) {
        return callback(new Error('Passwords must be 8-15 chars, include uppercase, lowercase, digit and one special char @ % * & _'))
      }
      return callback()
    }

    const validatePass2 = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('Please enter your password again'))
      }
      if (value.length < 8 || value.length > 15) {
        return callback(new Error('Your password must be 8 ~ 15 characters long'))
      }
      if (value !== this.registerForm.password) {
        return callback(new Error('The two passwords are inconsistent!'))
      }
      return callback()
    }

    return {
      registerForm: {
        email: '',
        verificationCode: '',
        password: '',
        confirmPassword: ''
      },
      registerRules: {
        verificationCode: [{ validator: verificationCode, trigger: ['blur', 'change'] }],
        email: [{ validator: validateEmail, trigger: ['blur', 'change'] }],
        password: [{ validator: validatePass, trigger: ['blur', 'change'] }],
        confirmPassword: [{ validator: validatePass2, trigger: ['blur', 'change'] }]
      },
      passwordType: 'password',
      agreePolicy: false,
      showPrivacyDialog: false,
      showServicesDialog: false,
      sending: false,
      sendDebounceTimer: null,
      sendCountdown: 0,
      sendTimer: null,
      creating: false,
      // success dialog state
      showSuccessDialog: false,
      successCountdown: 5,
      successTimer: null
    }
  },
  watch: {
    showSuccessDialog(val) {
      if (val) {
        this.successCountdown = 5
        this.startSuccessCountdown()
      } else {
        this.clearSuccessTimer()
      }
    }
  },
  beforeDestroy() {
    this.clearSendCountdown()
    this.clearSuccessTimer()
  },
  created() {
    if (this.$store.getters.isMX) {
      this.$router.push('/login')
    }
  },
  methods: {
    goLogin() {
      this.$router.push('/login')
    },
    openServices() {
      this.showServicesDialog = true
    },
    openPrivacy() {
      this.showPrivacyDialog = true
    },
    isEmailValid() {
      const email = this.registerForm.email || ''
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    onSendClick() {
      // 发送按钮做防抖并且只有邮箱格式正确时可触发
      if (!this.isEmailValid()) {
        this.$message.warning('Please enter a valid email before sending code')
        return
      }
      if (this.sendDebounceTimer) {
        // already waiting
        return
      }
      // debounce 800ms
      this.sendDebounceTimer = setTimeout(() => {
        this.doSendCode()
        clearTimeout(this.sendDebounceTimer)
        this.sendDebounceTimer = null
      }, 800)
    },
    doSendCode() {
      this.sending = true
      // prepare payload and encrypt on frontend (same pattern as login)
      const method = '1'
      const email = this.registerForm.email || ''
      const phone = ''
      const phone_country_code = ''
      const params = { method, email, phone, phone_country_code, timestamp: Date.now(), device: getDeviceHeader() }
      const aesKey = this.$commonFn.generateRandomString()
      const aesIV = this.$commonFn.generateRandomString()
      const aesKeyIv = JSON.stringify({ key: aesKey, iv: aesIV })
      const payload = {}
      try {
        payload.key = this.$commonFn.rsaEncrypt(aesKeyIv)
        payload.param = this.$commonFn.cryptoJSAesEncrypt(JSON.stringify(params), aesKey, aesIV)
        try {
          payload.sign = this.$commonFn.rsaSign(JSON.stringify(params))
        } catch (e) {
          // signing optional; continue
        }
      } catch (e) {
        this.sending = false
        this.$message.error('Encryption failed')
        return
      }
      // call API
      sendSelfRegisterOtp(payload).then(res => {
        this.sending = false
        this.$message.success(res.message || 'Verification code sent')
        // start 60s resend countdown
        this.sendCountdown = 60
        this.startSendCountdown()
      }).catch(err => {
        this.sending = false
        console.error(err)
      })
    },
    startSendCountdown() {
      this.clearSendCountdown()
      this.sendTimer = setInterval(() => {
        if (this.sendCountdown <= 1) {
          this.clearSendCountdown()
          this.sendCountdown = 0
        } else {
          this.sendCountdown--
        }
      }, 1000)
    },
    clearSendCountdown() {
      if (this.sendTimer) {
        clearInterval(this.sendTimer)
        this.sendTimer = null
      }
    },
    handleCreate() {
      // validate form first
      this.$refs.registerForm.validate((valid) => {
        if (!valid) {
          this.$message.warning('Please fix form errors before submitting')
          return
        }
        // perform front-end encryption and call create API
        this.creating = true
        const method = '1'
        const email = this.registerForm.email || ''
        const phone = ''
        const otp_code = this.registerForm.verificationCode || ''
        const password = this.registerForm.password || ''
        const params = { method, email, phone, otp_code, password, timestamp: Date.now() }
        const { cryptoJSAesEncrypt, rsaEncrypt, rsaSign, generateRandomString } = require('@/utils/encrypt')
        const aesKey = generateRandomString(16)
        const aesIV = generateRandomString(16)
        const aesKeyIv = JSON.stringify({ key: aesKey, iv: aesIV })
        const payload = {}
        try {
          payload.key = rsaEncrypt(aesKeyIv)
          payload.param = cryptoJSAesEncrypt(JSON.stringify(params), aesKey, aesIV)
          try {
            payload.sign = rsaSign(JSON.stringify(params))
          } catch (e) {
            // optional
          }
        } catch (e) {
          this.creating = false
          this.$message.error('Encryption failed')
          return
        }
        const { createSelfRegister } = require('@/api/user')
        createSelfRegister(payload).then(res => {
          this.creating = false
          this.$message.success(res.message || 'Register success')
          this.showSuccessDialog = true
          this.successCountdown = 5
          this.startSuccessCountdown()
        }).catch(err => {
          this.creating = false
          console.error(err)
        })
      })
    },
    startSuccessCountdown() {
      this.clearSuccessTimer()
      this.successTimer = setInterval(() => {
        if (this.successCountdown <= 1) {
          this.clearSuccessTimer()
          this.goToLoginFromDialog()
        } else {
          this.successCountdown--
        }
      }, 1000)
    },
    clearSuccessTimer() {
      if (this.successTimer) {
        clearInterval(this.successTimer)
        this.successTimer = null
      }
    },
    goToLoginFromDialog() {
      this.showSuccessDialog = false
      this.clearSuccessTimer()
      this.$router.push('/login')
    },
    onSuccessDialogClose(done) {
      // ensure timer cleanup when dialog closed by user
      this.clearSuccessTimer()
      done()
    }
  }
}
</script>

<style scoped>
.register-page-full{
  display:flex;
  height:100vh;
  width:100%;
  background: #f5f7fb;
}
.left-panel{
  width:50%;
  background: linear-gradient(98.94deg, #349EFF 0%, #306EDE 50.7%, #0124D9 100%);
  display:flex;
  align-items:center;
  justify-content:center;
  position:relative;
  overflow:hidden;
}
.brand-area{
  width:70%;
  max-width:520px;
}
.brand-logo{
  width:100%;
  display:block;
}
.right-panel{
  width:50%;
  display:flex;
  align-items:center;
  justify-content:center;
}
.card{
  width:520px;
  background:#fff;
  padding:36px 36px 40px 36px;
  border-radius:8px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.06);
  text-align:left;
}
.card-title{
  text-align:center;
  margin:0 0 18px 0;
  color:#1D3A71;
  font-size:20px;
  font-weight:700;
}
.verification-row{
  display:flex;
  gap:0;
  align-items:center;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  min-height: 48px;
}
.verification-input{
  flex:1;
  margin:0;
}
.send-btn{
  white-space:nowrap;
  border-radius:6px;
  padding:6px 12px;
  color:#2563EB;
}
.send-btn.link-style{
  background: transparent;
  color: #2563EB;
  padding: 0 18px;
  border-left: 1px solid #E6EAF0;
  height:48px;
  display:flex;
  align-items:center;
  border-radius: 0;
}
.verification-row .el-input{
  flex:1;
}
.verification-row .el-input--medium .el-input__inner{
  border: 1px solid transparent;
  box-shadow: none;
  height: 48px;
  padding: 12px 16px;
  font-size: 15px;
  background: transparent;
}
.verification-row .el-input__inner:focus{
  outline: none;
  box-shadow: none;
  border-color: transparent !important;
}
.agree-checkbox{
  color:#000000;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.agree-checkbox .el-checkbox__input{
  /* vertically align the native checkbox */
  margin-top: 6px;
  flex: 0 0 auto;
}
.agree-checkbox .el-checkbox__label{
  display: block;
  flex: 1 1 auto;
  margin-left: 0;
  white-space: normal;
  line-height: 1.6;
  word-break: break-word;
}
.policy-link{
  color: cornflowerblue;
  margin:0 4px;
  display:inline-block;
  white-space: normal;
  word-break: break-word;
}
.create-row{
  display:flex;
  justify-content:center;
  margin-top:8px;
}
.create-btn{
  width:220px;
  border-radius:24px;
  color:#fff;
}
.create-btn.full{
  width:100%;
  height:48px;
  border-radius:8px;
  font-size:16px;
  font-weight:600;
}
.signin-line{
  text-align:center;
  margin-top:18px;
  color:#7A869E;
}
.footer-copy{
  text-align:center;
  margin-top:18px;
  color:#98A1B4;
  font-size:12px;
}

/* Privacy dialog wrapper style per UI */
/* privacy dialog styles moved into the PrivacyPolicy component */

@media screen and (max-width: 900px) {
  .left-panel{ display:none; }
  .right-panel{ width:100%; padding:36px; }
  .card{ width:100%; max-width:520px; }
}
</style>

<!-- Privacy dialog placed at end of file so it renders in the register page -->
